import React from 'react';
import { Grid } from '@mui/material';
import type { DashboardSummary } from '../../services/dashboard-service';
import { NavigateFn, SectionSkeleton } from './shared';
import { NeedsAttention } from './NeedsAttention';
import { CashFlowSummary, NetWorthSummary } from './NetWorthAndCashFlow';
import { BillsThisMonth, SpendingByCategory } from './BillsAndSpending';
import { LatestAnalysis, RecentTransactions } from './AnalysisAndActivity';

/**
 * Sections 1-7 of the home dashboard (issue #27), from one summary.
 *
 * Paired left-right on wide screens so reading order is preserved row by row
 * (net worth | bills, spending | analysis, cash flow | activity) and stacked
 * in the same order on a phone.
 */
export const DashboardSections: React.FC<{
  summary: DashboardSummary | null;
  isLoading: boolean;
  onNavigate: NavigateFn;
}> = ({ summary, isLoading, onNavigate }) => {
  if (!summary) {
    if (!isLoading) return null;
    return (
      <Grid container spacing={3} aria-busy="true">
        {['Net worth', 'Bills this month', 'Spending by category', 'Latest analysis'].map((t) => (
          <Grid item xs={12} md={6} key={t}><SectionSkeleton title={t} /></Grid>
        ))}
      </Grid>
    );
  }
  return (
    <>
      <NeedsAttention items={summary.needs_attention} onNavigate={onNavigate} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <NetWorthSummary netWorth={summary.net_worth} onNavigate={onNavigate} />
        </Grid>
        <Grid item xs={12} md={7}>
          <BillsThisMonth bills={summary.bills} onNavigate={onNavigate} />
        </Grid>
        <Grid item xs={12} md={7}>
          <SpendingByCategory spending={summary.spending} onNavigate={onNavigate} />
        </Grid>
        <Grid item xs={12} md={5}>
          <LatestAnalysis analysis={summary.latest_analysis} onNavigate={onNavigate} />
        </Grid>
        <Grid item xs={12} md={5}>
          <CashFlowSummary cashFlow={summary.cash_flow} onNavigate={onNavigate} />
        </Grid>
        <Grid item xs={12} md={7}>
          <RecentTransactions txns={summary.recent_activity} onNavigate={onNavigate} />
        </Grid>
      </Grid>
    </>
  );
};
